/**
 * Dancer Contract Service
 * Manages tax and bank information for dancers with country-specific validation
 * Follows Service Layer Architecture with Result pattern and structured logging
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { Result } from '@kit/shared/result';
import { BaseService } from '@kit/shared/services';
import type { Database } from '@kit/supabase/database';

// Type definitions
type ContractDetails =
  Database['public']['Tables']['artist_contract_details']['Row'];
type CreateContractInput =
  Database['public']['Tables']['artist_contract_details']['Insert'];
type CountryRequirements =
  Database['public']['Tables']['country_contract_requirements']['Row'];

export interface SaveContractDetailsInput {
  countryCode: string;
  taxId?: string;
  taxDocumentPath?: string;
  registrationType?: 'sole_trader' | 'company' | 'other';
  registrationNumber?: string;
  vatNumber?: string;
  iban?: string;
  bic?: string;
  bankName?: string;
  bankAddress?: string;
  accountHolderName?: string;
  routingNumber?: string;
  accountNumber?: string;
  sortCode?: string;
}

/**
 * Service class for handling dancer contract details operations
 * Provides type-safe operations with proper error handling and IBAN validation
 */
export class DancerContractService extends BaseService {
  constructor(client: SupabaseClient<Database>) {
    super(client);
  }

  /**
   * Get contract details for a user
   * Uses execute() for consistent error handling
   */
  async getContractDetails(
    userId: string,
  ): Promise<Result<ContractDetails | null>> {
    return this.execute(async () => {
      const context = { userId, operation: 'getContractDetails' };
      this.log('info', 'Getting contract details for user', context);

      const { data, error } = await this.client
        .from('artist_contract_details')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        this.log('error', 'Failed to get contract details', {
          ...context,
          error,
        });
        throw this.handleDatabaseError(error);
      }

      this.log('info', 'Contract details retrieved', {
        ...context,
        found: !!data,
      });

      return data;
    }, 'Failed to get contract details');
  }

  /**
   * Save or update contract details for a user
   * Creates new record or updates existing one
   * Uses execute() for consistent error handling
   */
  async saveContractDetails(
    userId: string,
    input: SaveContractDetailsInput,
  ): Promise<Result<ContractDetails>> {
    return this.execute(async () => {
      const context = { userId, operation: 'saveContractDetails' };
      this.log('info', 'Saving contract details', context);

      // Map input to database format
      const contractData: Omit<CreateContractInput, 'id' | 'created_at'> = {
        user_id: userId,
        country_code: input.countryCode,
        tax_id: input.taxId || null,
        tax_document_path: input.taxDocumentPath || null,
        registration_type: input.registrationType || null,
        registration_number: input.registrationNumber || null,
        vat_number: input.vatNumber || null,
        iban: input.iban || null,
        bic: input.bic || null,
        bank_name: input.bankName || null,
        bank_address: input.bankAddress || null,
        account_holder_name: input.accountHolderName || null,
        routing_number: input.routingNumber || null,
        account_number: input.accountNumber || null,
        sort_code: input.sortCode || null,
        updated_at: new Date().toISOString(),
      };

      // Upsert (insert or update)
      const { data, error } = await this.client
        .from('artist_contract_details')
        .upsert(contractData, {
          onConflict: 'user_id',
        })
        .select()
        .single();

      if (error) {
        this.log('error', 'Failed to save contract details', {
          ...context,
          error,
        });
        throw this.handleDatabaseError(error);
      }

      this.log('info', 'Contract details saved successfully', {
        ...context,
        detailsId: data.id,
      });

      return data;
    }, 'Failed to save contract details');
  }

  /**
   * Get country requirements by country code
   * Uses execute() for consistent error handling
   */
  async getCountryRequirements(
    countryCode: string,
  ): Promise<Result<CountryRequirements | null>> {
    return this.execute(async () => {
      const context = { countryCode, operation: 'getCountryRequirements' };
      this.log('info', 'Getting country requirements', context);

      const { data, error } = await this.client
        .from('country_contract_requirements')
        .select('*')
        .eq('country_code', countryCode)
        .maybeSingle();

      if (error) {
        this.log('error', 'Failed to get country requirements', {
          ...context,
          error,
        });
        throw this.handleDatabaseError(error);
      }

      this.log('info', 'Country requirements retrieved', {
        ...context,
        found: !!data,
      });

      return data;
    }, 'Failed to get country requirements');
  }

  /**
   * Get all supported countries and their requirements
   * Uses execute() for consistent error handling
   */
  async getAllCountryRequirements(): Promise<Result<CountryRequirements[]>> {
    return this.execute(async () => {
      const context = { operation: 'getAllCountryRequirements' };
      this.log('info', 'Getting all country requirements', context);

      const { data, error } = await this.client
        .from('country_contract_requirements')
        .select('*')
        .order('country_name');

      if (error) {
        this.log('error', 'Failed to get all country requirements', {
          ...context,
          error,
        });
        throw this.handleDatabaseError(error);
      }

      this.log('info', 'All country requirements retrieved', {
        ...context,
        count: data.length,
      });

      return data;
    }, 'Failed to get all country requirements');
  }

  /**
   * Validate IBAN format and checksum
   * Uses MOD 97 algorithm for checksum validation
   * Returns true if IBAN is valid, false otherwise
   */
  validateIBAN(iban: string): boolean {
    // Remove spaces and convert to uppercase
    const cleaned = iban.replace(/\s/g, '').toUpperCase();

    // Check length (varies by country, 15-34 chars)
    if (cleaned.length < 15 || cleaned.length > 34) {
      return false;
    }

    // Check format (2 letters + 2 digits + alphanumeric)
    if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(cleaned)) {
      return false;
    }

    // Move first 4 chars to end
    const rearranged = cleaned.slice(4) + cleaned.slice(0, 4);

    // Convert letters to numbers (A=10, B=11, etc.)
    const numeric = rearranged
      .split('')
      .map((c) => {
        const code = c.charCodeAt(0);
        return code >= 65 ? (code - 55).toString() : c;
      })
      .join('');

    // MOD 97 check using chunking for large numbers
    let remainder = numeric;
    while (remainder.length > 2) {
      const block = remainder.slice(0, 9);
      remainder = (parseInt(block, 10) % 97).toString() + remainder.slice(9);
    }

    return parseInt(remainder, 10) % 97 === 1;
  }

  /**
   * Validate tax ID against country-specific pattern
   * Returns true if tax ID matches pattern, false otherwise
   */
  async validateTaxId(
    taxId: string,
    countryCode: string,
  ): Promise<Result<boolean>> {
    return this.execute(async () => {
      const context = { countryCode, operation: 'validateTaxId' };
      this.log('info', 'Validating tax ID', context);

      // Get country requirements
      const requirementsResult = await this.getCountryRequirements(countryCode);
      if (!requirementsResult.isSuccess) {
        return false; // No requirements found, can't validate
      }

      const requirements = requirementsResult.getValue();
      if (!requirements || !requirements.tax_id_pattern) {
        return true; // No pattern defined, consider valid
      }

      // Test against regex pattern
      const pattern = new RegExp(requirements.tax_id_pattern);
      const isValid = pattern.test(taxId);

      this.log('info', 'Tax ID validation result', {
        ...context,
        isValid,
      });

      return isValid;
    }, 'Failed to validate tax ID');
  }
}
